import createAuthClient from "@adsk/identity-web-sdk"
import type { AuthClient } from "@adsk/identity-web-sdk"

let _authClientPromise: Promise<AuthClient> | null = null
let _clientId: string | null = null
let _env: string | null = null

const _ADSK_CLIENT_CONFIG_KEY = "autodesk_client_config"

/**
 * Thrown by autodeskLoginWithPopup() when the browser blocks the popup before the
 * Autodesk Identity SDK is even invoked. Distinguishing this from the SDK's own
 * error classes (PopupCancelledError, PopupTimeoutError, GenericError) lets callers
 * surface this specific, actionable message to the user instead of a generic one.
 */
export class PopupBlockedError extends Error {}

/**
 * Thrown by autodeskLoginWithPopup() when client.loginWithPopup() doesn't settle within
 * LOGIN_POPUP_TIMEOUT_MS. This is a safety net for a known SDK/browser interaction: if
 * Autodesk's auth pages send a restrictive Cross-Origin-Opener-Policy header, the SDK's
 * internal popup-closed polling can throw on every tick (uncaught, inside a setInterval)
 * instead of ever rejecting, so its own promise never settles and the caller's loading
 * state would otherwise be stuck indefinitely instead of just until the SDK's own
 * 15-minute PopupTimeoutError.
 */
export class PopupTrackingLostError extends Error {}

const POPUP_BLOCKED_MESSAGE = "The Autodesk authentication pop-up was blocked. Please allow pop-ups from Rhumbix to Sign in with Autodesk."
const POPUP_TRACKING_LOST_MESSAGE = "Autodesk authentication failed. Please try again."
const LOGIN_POPUP_TIMEOUT_MS = 120_000

/**
 * Return the cached auth client Promise, creating it if the config has changed.
 *
 * Stores a Promise so that repeated calls with the same config share a single
 * SDK initialisation without triggering duplicate createAuthClient calls.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID registered in the Autodesk Developer Portal.
 * env (string): Target environment — "prd" for production, "stg" for staging.
 *
 * Returns:
 * Promise<AuthClient>: Resolves to the Autodesk Identity auth client instance.
 */
function getOrCreateAuthClientPromise(clientId: string, env: string): Promise<AuthClient> {
    if (!_authClientPromise || _clientId !== clientId || _env !== env) {
        _authClientPromise = createAuthClient({
            client_id: clientId,
            redirect_uri: `${window.location.origin}/oauth/callback`,
            redirect_uri_popup: `${window.location.origin}/oauth/callback`,
            // The SDK expects "prod" for production, "stg" for staging.
            env: env === "prd" ? "prod" : "stg",
            // The SDK appends 'openid' automatically, so the effective scope is
            // 'data:read profapi:core-std-profile:read openid'.
            // 'profapi:core-std-profile:read' is required by Autodesk for the
            // /userinfo endpoint to return the user's email and profile fields.
            scope: "data:read openid profapi:core-std-profile:read",
            useRefreshTokens: true,
            useReactContext: true,
            cacheLocation: "localstorage",
            enableLocalNetworkAccess: window.location.hostname === "localhost",
        })
        _clientId = clientId
        _env = env
    }
    return _authClientPromise
}

/**
 * Persist the Autodesk client configuration to localStorage so that
 * autodeskLogout() can revoke tokens even after a full page refresh.
 *
 * Call this immediately after a successful Autodesk login.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID.
 * env (string): Target environment — "prd" or "stg".
 */
export function storeAutodeskClientConfig(clientId: string, env: string): void {
    try {
        localStorage.setItem(_ADSK_CLIENT_CONFIG_KEY, JSON.stringify({ clientId, env }))
    } catch {
        // Non-fatal: if localStorage is unavailable, logout will fall back to module state.
    }
}

/**
 * Revoke Autodesk tokens and clear the SDK's local state.
 *
 * Reads the stored client configuration from module state (available within
 * the same page session) or localStorage (available after a page refresh).
 * Calls client.logout({ localOnly: true }), which revokes both the access token and
 * refresh token (if one was issued) that the SDK cached during loginWithPopup — via
 * Autodesk's /authentication/v2/revoke endpoint — then clears all SDK localStorage
 * keys. localOnly skips the browser-redirect-based /authentication/v2/logout call,
 * since revoke doesn't need cookies/browser context. The function is best-effort:
 * any error is silently swallowed so that the caller's logout flow (e.g. POST
 * /api/v3/users/logout/) always continues. The stored config entry is removed from
 * localStorage on completion regardless of whether the SDK call succeeded.
 *
 * Returns:
 * Promise<void>
 */
export async function autodeskLogout(): Promise<void> {
    let clientId = _clientId
    let env = _env

    if (!clientId || !env) {
        try {
            const stored = localStorage.getItem(_ADSK_CLIENT_CONFIG_KEY)
            if (stored) {
                const parsed = JSON.parse(stored) as { clientId?: string; env?: string }
                clientId = parsed.clientId ?? null
                env = parsed.env ?? null
            }
        } catch {
            // Non-fatal: proceed without Autodesk SDK logout.
        }
    }

    if (!clientId || !env) {
        return
    }

    try {
        const client = await getOrCreateAuthClientPromise(clientId, env)
        await client.logout({ localOnly: true })
    } catch {
        // Non-fatal: Rhumbix logout continues regardless of Autodesk SDK errors.
    } finally {
        try {
            localStorage.removeItem(_ADSK_CLIENT_CONFIG_KEY)
        } catch {
            // Ignore localStorage errors.
        }
    }
}

// getAccessTokenSilently() can drive the SDK's hidden-iframe silent-auth flow, which takes
// multiple seconds (or hits a SecurityError/timeout) when the iframe fails. Since callers
// treat this as a best-effort, no-user-interaction lookup, bound it with a timeout so it
// can never block a caller (e.g. the cookie-consent dialog) for more than this long.
const SILENT_TOKEN_TIMEOUT_MS = 3000

/**
 * Whether this browser session signed in through Autodesk Identity.
 *
 * storeAutodeskClientConfig() is only called from the Rhumbix "Sign in with Autodesk"
 * login flow, and autodeskLogout() always removes it -- so its presence reflects *this*
 * session's login and survives account switches on a shared browser. The SDK can't answer
 * that question itself: it reports browser-wide Autodesk SSO state, which may belong to
 * another Autodesk product or a previously signed-in account.
 */
function hasStoredAutodeskLogin(): boolean {
    try {
        return localStorage.getItem(_ADSK_CLIENT_CONFIG_KEY) !== null
    } catch {
        return false
    }
}

/**
 * Best-effort retrieval of a valid Autodesk access token for the current browser
 * session, without any user interaction or redirect.
 *
 * Used by adpConsent.ts to pass a "Named User" token to the ADP Web SDK's
 * loadUserConsentPreferences(userToken), so the consent decision is read and written
 * against the user's profile.autodesk.com account. Returning null means we can't identify
 * the account, and the caller treats that as "no consent decision available" rather than
 * assuming one.
 *
 * Skips calling the SDK entirely (see hasStoredAutodeskLogin()) when this session's own
 * login didn't go through Autodesk.
 *
 * Bounded by SILENT_TOKEN_TIMEOUT_MS (see above) so a slow or failing SDK check can't
 * block callers indefinitely; a timeout is treated the same as "not authenticated."
 *
 * Callers that read repeatedly should cache the result for the page load rather than
 * calling this per read -- each call can start a fresh silent-auth flow.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID (window.autodesk_oauth_client_id).
 * env (string): Target environment — "prd" or "stg" (window.autodesk_oauth_env).
 *
 * Returns:
 * Promise<string | null>: A valid Autodesk access token, or null if the current
 *   session isn't Autodesk-authenticated, a token couldn't be retrieved silently, or
 *   the check didn't complete within SILENT_TOKEN_TIMEOUT_MS.
 */
export async function getAutodeskAccessTokenSilently(clientId: string, env: string): Promise<string | null> {
    if (!clientId || !env || !hasStoredAutodeskLogin()) {
        return null
    }
    const attemptSilentToken = async (): Promise<string | null> => {
        const client = await getOrCreateAuthClientPromise(clientId, env)
        // Deliberately no isAuthenticated() pre-check: it starts a silent-auth flow of its
        // own, doubling the /as/flows round trips for one token (and 404ing when the second
        // flow races the first). getAccessTokenSilently() throws for an unauthenticated
        // session anyway, which the catch below turns into the same null.
        return await client.getAccessTokenSilently()
    }
    try {
        return await Promise.race([attemptSilentToken(), new Promise<null>(resolve => setTimeout(() => resolve(null), SILENT_TOKEN_TIMEOUT_MS))])
    } catch {
        // Non-fatal: callers fail closed and skip tracking rather than surfacing an error.
        return null
    }
}

/**
 * Open an Autodesk Identity popup via the SDK's built-in loginWithPopup, authenticate
 * the user, and return the resulting access and ID tokens for exchange with the Rhumbix
 * backend.
 *
 * Delegates entirely to client.loginWithPopup(), which handles PKCE, state generation,
 * popup opening, URL polling, and code exchange internally.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID from the backend availability response.
 * env (string): Target environment — "prd" for production, "stg" for staging.
 * email (string): Optional login hint to pre-fill the Autodesk sign-in form.
 *
 * Returns:
 * Promise<{accessToken: string, idToken: string|null}>: Resolves with the Autodesk
 *   access token and OIDC ID token on successful authentication.  The ID token is
 *   best-effort and may be null if the SDK cache is unavailable.
 *
 * Throws:
 * PopupBlockedError: If the browser blocks the popup, either before the SDK is invoked
 *   or when the SDK itself opens the popup (normalized from the SDK's GenericError with
 *   error === "open_popup_failed").
 * PopupCancelledError: If the popup is closed before authentication completes.
 * PopupTimeoutError: If the user does not complete authentication within 15 minutes.
 * PopupTrackingLostError: If client.loginWithPopup() doesn't settle within
 *   LOGIN_POPUP_TIMEOUT_MS. The SDK detects a closed popup by polling its `closed`
 *   property; if Autodesk's auth pages send a restrictive Cross-Origin-Opener-Policy
 *   header, that property access can throw on every poll tick instead of ever
 *   resolving/rejecting, silently hanging the SDK's promise until its own 15-minute
 *   PopupTimeoutError. This timeout bounds that failure mode to something a user won't
 *   give up waiting on.
 */
export async function autodeskLoginWithPopup(clientId: string, env: string, email: string, isCreateAccount: boolean = false): Promise<{ accessToken: string; idToken: string | null }> {
    const testPopup = window.open("about:blank", "_blank", "width=1,height=1")
    if (!testPopup) {
        throw new PopupBlockedError(POPUP_BLOCKED_MESSAGE)
    }
    testPopup.close()

    const client = await getOrCreateAuthClientPromise(clientId, env)

    let accessToken: string
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    try {
        accessToken = await Promise.race([
            client.loginWithPopup(email ? { prompt: "login", login_hint: email, authoptions: { email, isregext: isCreateAccount ? "true" : "false" } } : {}),
            new Promise<never>((_resolve, reject) => {
                timeoutId = setTimeout(() => reject(new PopupTrackingLostError(POPUP_TRACKING_LOST_MESSAGE)), LOGIN_POPUP_TIMEOUT_MS)
            }),
        ])
    } catch (e) {
        // The await above can cost us the transient user-activation gesture from the
        // click that started this flow (Safari drops it after any await; Chrome after
        // enough of a delay), so the SDK's own popup can be blocked even though the
        // testPopup check above succeeded. The SDK surfaces that as a GenericError with
        // error === "open_popup_failed" rather than rejecting loginWithPopup with a
        // popup-specific error class, so normalize it to PopupBlockedError here too.
        if (e && typeof e === "object" && "error" in e && e.error === "open_popup_failed") {
            throw new PopupBlockedError(POPUP_BLOCKED_MESSAGE)
        }
        throw e
    } finally {
        clearTimeout(timeoutId)
    }

    // Retrieve the OIDC ID token from the SDK cache.  It was stored during the PKCE
    // token exchange above and is guaranteed by the OIDC spec to include the 'sub'
    // claim (Autodesk user ID) plus profile fields when the email/profile scopes are
    // present.  Sending it to the backend avoids the need to call /userinfo for user
    // identification.  This call is best-effort — if the cache is unavailable the
    // backend falls back to /userinfo.
    let idToken: string | null = null
    try {
        idToken = await client.getIDTokenSilently()
    } catch {
        // Non-fatal: the backend will fall back to /userinfo to identify the user.
    }

    return { accessToken, idToken }
}
